import { EncoderToolType } from '../types';

export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  allowedTypes?: string[];
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export class CommonValidator {
  static validateText(text: string, rules: ValidationRule): ValidationResult {
    const errors: string[] = [];

    if (rules.required && !text.trim()) {
      errors.push('Text is required');
    }

    if (rules.minLength && text.length < rules.minLength) {
      errors.push(`Text must be at least ${rules.minLength} characters long`);
    }

    if (rules.maxLength && text.length > rules.maxLength) {
      errors.push(`Text must not exceed ${rules.maxLength} characters`);
    }

    if (rules.pattern && !rules.pattern.test(text)) {
      errors.push('Text format is invalid');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  static validateFile(file: File, rules: ValidationRule): ValidationResult {
    const errors: string[] = [];

    if (rules.required && !file) {
      errors.push('File is required');
    }

    if (file && rules.allowedTypes && !rules.allowedTypes.includes(file.type)) {
      errors.push(`File type must be one of: ${rules.allowedTypes.join(', ')}`);
    }

    if (file && rules.maxLength && file.size > rules.maxLength) {
      errors.push(`File size must not exceed ${rules.maxLength} bytes`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  static getValidationRules(toolType: EncoderToolType): ValidationRule {
    switch (toolType) {
      case EncoderToolType.GZIP_COMPRESSION:
        return {
          required: true,
          minLength: 1,
          maxLength: 10 * 1024 * 1024 // 10MB
        };
      case EncoderToolType.BASE64_TEXT:
        return {
          required: false, // Allow empty strings for Base64
          maxLength: 1024 * 1024 // 1MB
        };
      case EncoderToolType.URL_ENCODER:
        return {
          required: true,
          maxLength: 2048
        };
      case EncoderToolType.JWT_TOKEN:
        return {
          required: true,
          maxLength: 4096
        };
      case EncoderToolType.HTML_ENTITY:
        return {
          required: true,
          maxLength: 1024 * 1024 // 1MB
        };
      case EncoderToolType.BASE64_IMAGE:
        return {
          required: true,
          allowedTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'],
          maxLength: 10 * 1024 * 1024 // 10MB
        };
      case EncoderToolType.QR_CODE:
        return {
          required: true,
          maxLength: 2048
        };
      case EncoderToolType.CERTIFICATE_DECODER:
        return {
          required: true,
          maxLength: 50 * 1024 // 50KB
        };
      default:
        return {
          required: true
        };
    }
  }
}